<template>
  <div>
    <div
      v-if="isLoadingScreen"
      class="flex min-h-screen flex-col items-center justify-center"
    >
      <div>
        <RevSpinner class="m-auto" />
      </div>
      <p class="body-2">
        {{ i18n(translations.loading) }}
      </p>
    </div>

    <div v-if="!isLoadingScreen" class="flex min-h-screen">
      <div class="hidden lg:block lg:w-[35.5rem] lg:pl-160 lg:pt-32">
        <RevLink
          class="flex items-center lg:p-12"
          data-test="back-link"
          @click="goBack"
        >
          <IconArrowLeft size="small" />
          <span class="ml-12">
            {{ backLink.label }}
          </span>
        </RevLink>

        <DeviceSummary
          v-if="displaySummary"
          data-test="device-summary"
          :summary="summary"
        />

        <RevIllustration
          alt="summary"
          :class="summaryIllustrationClass"
          :height="180"
          src="/img/buyback/buybackFunnel.svg"
          :width="329"
        />
      </div>
      <div
        ref="container"
        class="bg-surface-default-low grow scroll-mt-96 p-16 lg:w-auto lg:px-72 lg:py-32"
      >
        <RevStepper
          v-if="activeStepName"
          :active-step="activeStepName"
          alternative-text-back="back"
          alternative-text-close="close"
          alternative-text-completed="completed"
          :alternative-text-current="activeStepName"
          class="mb-24 md:mx-auto md:w-[39.5rem]"
          data-test="stepper"
          :has-back="stepperHasBackButton"
          :steps="steps"
          @back="goBack"
        />

        <QuestionsForm
          v-if="displayFunnel"
          :active-step="activeStep"
          class="md:m-auto md:w-[39.5rem]"
          :funnel="funnel"
          @next-question="getNextQuestion"
          @next-step="getNextStep"
          @submit-answers="getOffer"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { createError, useRoute, useRouter } from '#imports'
import { computed, ref } from 'vue'

import { HttpEvent, buybackCustomerAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useBreakpoint } from '@backmarket/utils/composables/useBreakpoint'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { stringify } from '@backmarket/utils/query-string/stringify'
import { tw } from '@backmarket/utils/string/tw'
import { urlHashToObject } from '@backmarket/utils/url/urlHashToObject'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevSpinner } from '@ds/components/Spinner'
import { RevStepper } from '@ds/components/Stepper'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'

import DeviceSummary from '../components/DeviceSummary/DeviceSummary.vue'
import QuestionsForm, {
  type NextQuestionPayload,
  type NextStepPayload,
  type SubmitAnswersPayload,
} from '../components/QuestionsForm/QuestionsForm.vue'
import { ROUTE_NAMES } from '../constants'

import translations from './DeviceAssessment.translations'

const i18n = useI18n()
const router = useRouter()
const route = useRoute()
const breakpoint = useBreakpoint()
const tracking = useTracking()

const scrollToTop = ref(false)
const isErrorToastOpened = ref(false)
const isLoadingScreen = ref(true)

const { params } = route

const routeHashPayload = computed(() => {
  return Object.entries(urlHashToObject(route.hash.substring(1))).reduce(
    (acc, [key, value]) => {
      return {
        ...acc,
        [key]: Array.isArray(value) && value.length <= 1 ? value[0] : value,
      }
    },
    {},
  )
})

const container = ref<HTMLElement | null>(null)

const queryParamsPayload = computed(() => ({
  ...routeHashPayload.value,
  category: params.id,
}))

const { data: buybackQuestions, error } = await useHttpFetch(
  buybackCustomerAPI.getQuestionsV3,
  {
    queryParams: queryParamsPayload,
    pathParams: {
      kind: 'regular',
    },
    server: false,
    watch: [routeHashPayload],
    onEvent: (event) => {
      if (event === HttpEvent.Success) {
        isLoadingScreen.value = false
      }

      if (event === HttpEvent.Success && scrollToTop.value) {
        container.value?.scrollIntoView({
          block: 'start',
        })
        scrollToTop.value = false
      }
    },
  },
)

if (error.value) {
  // fatal: true option redirects to Error view
  throw createError({
    statusCode: error.value.statusCode,
    statusMessage: error.value.message,
    fatal: true,
  })
}

const funnel = computed(() => buybackQuestions.value?.funnel || [])
const summary = computed(() => buybackQuestions.value?.summary || [])

const displaySummary = computed(() => {
  return !isEmpty(summary.value)
})

const summaryIllustrationClass = computed(() => {
  return displaySummary.value ? tw`mt-[8.4375rem]` : tw`mt-72`
})

const backLink = computed(() => {
  const { questions = [] } =
    (funnel.value && funnel.value.find(({ step }) => step?.active)) || {}
  const previousStep = funnel.value.findIndex(({ step }) => step?.active) - 1

  const hashParams: Record<string, boolean | string> = {
    ...routeHashPayload.value,
    nextStep: 'false',
  }
  const isNotAtFirstStep = previousStep >= 0
  const toRoute = isNotAtFirstStep
    ? route
    : {
        name: 'buyback',
        params: {
          pageName: 'home',
        },
      }

  questions.forEach(({ key }) => {
    delete hashParams[key]
  })

  return {
    label:
      (funnel.value[previousStep] && funnel.value[previousStep]?.step?.label) ||
      i18n(translations.goBack),
    to: {
      ...toRoute,
      ...(isNotAtFirstStep && {
        hash: `#${stringify(hashParams)}`,
      }),
    },
  }
})

const steps = computed(() => {
  return funnel.value.map(({ step }) => {
    if (!step) {
      return {
        label: '',
        name: '',
      }
    }

    return {
      label: step.label,
      name: step.key,
    }
  })
})

const activeStep = computed(() => {
  const activeFunnelStep = funnel.value.find(({ step }) => step?.active)

  return activeFunnelStep?.step || null
})

const activeStepName = computed(() => {
  return activeStep.value?.key || ''
})

const stepperHasBackButton = computed(() => {
  return breakpoint.value <= 2
})

const displayFunnel = computed(() => {
  return !isEmpty(funnel.value)
})

const goBack = () => {
  router.replace(backLink.value.to)
}

const getNextQuestion = ({
  formPayload = {},
  reset = [],
}: NextQuestionPayload) => {
  const formGlobal = { ...routeHashPayload.value, ...formPayload }

  if (!isEmpty(reset)) {
    reset.forEach((key) => {
      delete formGlobal[key as keyof typeof formGlobal]
    })
  }

  router.replace({
    ...route,
    hash: `#${stringify({ ...formGlobal })}`,
  })
}

const formatTrackingPayload = (payload: Record<string, string | string[]>) => {
  const formattedPayload = {
    ...payload,
  }
  delete formattedPayload.nextStep

  return formattedPayload
}

const getNextStep = ({
  formPayload,
  currentStep,
  shouldScrollToTop,
}: NextStepPayload) => {
  if (formPayload.nextStep) {
    tracking.trackClick({
      zone: 'buyback',
      name: currentStep || '',
      value: formatTrackingPayload({
        category: route.params.id,
        ...routeHashPayload.value,
        ...formPayload,
      }),
    })
  }
  getNextQuestion({ formPayload, reset: [] })

  scrollToTop.value = shouldScrollToTop
}

const getOffer = async ({ formPayload, currentStep }: SubmitAnswersPayload) => {
  const queryParams = {
    category: params.id,
    ...routeHashPayload.value,
    ...formPayload,
  }

  const { model: name, ...restPayload } = formatTrackingPayload(queryParams)
  tracking.trackBuybackDeviceSpecifications({
    product: {
      ...restPayload,
      name,
    },
  })

  tracking.trackClick({
    zone: 'buyback',
    name: currentStep || '',
    value: formatTrackingPayload(queryParams),
  })

  tracking.trackClick({
    zone: 'buyback',
    name: 'see_estimate',
    value: formatTrackingPayload(queryParams),
  })

  try {
    const { data, status } = await useHttpFetch(
      buybackCustomerAPI.postBuybackAnswers,
      {
        body: queryParams,
      },
    )

    if (data.value && status.value === 'success') {
      router.push({
        name: ROUTE_NAMES.OFFER,
        params: {
          ...route.params,
          offerId: data.value.sourcingListingId,
        },
        hash: `#${stringify(queryParams)}`,
      })
    }

    if (data.value === undefined && status.value === 'success') {
      tracking.trackBuybackNoOffer({
        value: formatTrackingPayload(queryParams),
      })

      router.push({
        name: ROUTE_NAMES.NO_OFFER,
        params: route.params,
      })
    }
  } catch (e) {
    isErrorToastOpened.value = true
  }
}
</script>
